import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';

import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';

import {
  ServicePackageService,
  ServicePackage,
  ServicePackagePurchase,
} from '@beautyapp/core';
import { SellPackageModalComponent } from '../sell-package-modal/sell-package-modal.component';
import { openPackageFormModal } from '../package-form/open-package-form-modal';

@Component({
  selector: 'beautyapp-package-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzCardModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzTableModule,
    NzDescriptionsModule,
    NzSpaceModule,
    NzPopconfirmModule,
    NzPageHeaderModule,
    NzSpinModule,
  ],
  templateUrl: './package-view.component.html',
})
export class PackageViewComponent {
  private packageService = inject(ServicePackageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private modal = inject(NzModalService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);

  packageId = signal(this.route.snapshot.paramMap.get('id')!);

  packageResource = rxResource({
    params: () => ({ id: this.packageId() }),
    stream: ({ params }) =>
      this.packageService.getById(params.id).pipe(catchError(() => of(null))),
  });

  purchasesResource = rxResource({
    params: () => ({ packageId: this.packageId() }),
    stream: ({ params }) =>
      this.packageService
        .getPurchases({ packageId: params.packageId, pageSize: 10 })
        .pipe(
          catchError(() =>
            of({
              data: [],
              meta: { page: 1, pageSize: 10, pageCount: 0, itemCount: 0 },
            })
          )
        ),
  });

  loading = computed(() => this.packageResource.isLoading());
  pkg = computed(() => this.packageResource.value() as ServicePackage | null);
  purchases = computed(
    () => (this.purchasesResource.value() as any)?.data || []
  );

  goBack(): void {
    this.router.navigate(['/service-packages']);
  }

  editPackage(): void {
    openPackageFormModal(this.modal, {
      packageId: this.packageId(),
      title: this.translate.instant('servicePackages.form.editTitle'),
    }).afterClose.subscribe((saved) => {
      if (saved) this.packageResource.reload();
    });
  }

  deletePackage(): void {
    this.packageService.delete(this.packageId()).subscribe({
      next: () => {
        this.message.success(
          this.translate.instant('servicePackages.messages.deleted')
        );
        this.goBack();
      },
      error: () => {
        this.message.error(
          this.translate.instant('servicePackages.messages.deleteError')
        );
      },
    });
  }

  sellPackage(): void {
    const pkg = this.pkg();
    if (!pkg) return;

    const modalRef = this.modal.create({
      nzTitle: this.translate.instant('servicePackages.sellModal.title'),
      nzContent: SellPackageModalComponent,
      nzWidth: 600,
      nzFooter: null,
      nzData: {
        packageId: pkg.id,
        packageName: pkg.name,
        packagePrice: pkg.price,
      },
    });

    modalRef.afterClose.subscribe((result) => {
      if (result?.success) {
        this.message.success(
          this.translate.instant('servicePackages.messages.sold')
        );
        this.packageResource.reload();
        this.purchasesResource.reload();
      }
    });
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      ACTIVE: 'green',
      USED: 'blue',
      CANCELLED: 'red',
      EXPIRED: 'default',
    };
    return colors[status] || 'default';
  }

  getTotalItemsCount(pkg: ServicePackage): number {
    return pkg.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  }
}
